<script setup lang="ts">
import { hasPermission } from '@permission/hasPermission';
import { AlertsFeaturesAlertAlertFrequentie as Frequentie } from '@services/alerts-public/orval-api/model/alertsFeaturesAlertAlertFrequentie';
import { AlertsFeaturesAlertPolisTypeOntvanger as Ontvanger } from '@services/alerts-public/orval-api/model/alertsFeaturesAlertPolisTypeOntvanger';
import {
  usePolisAlertsCreateForPolis,
  usePolisAlertsGetAlertvariabelen,
  usePolisAlertsUpdate,
} from '@services/alerts-public/orval-api/polis-alerts/polis-alerts';
import { isProblemDetail } from '@services/base/apiService';
import { usePolisGetPolissen } from '@services/polis-public/orval-api/polis-api/polis-api';
import { VButton } from '@shared/component/VButton';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSidePanel from '@shared/component/VSidePanel/VSidePanel.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import MultiCheckboxField from '@shared/form/CheckboxField/MultiCheckboxField.vue';
import SelectField from '@shared/form/SelectField/SelectField.vue';
import TextField from '@shared/form/TextField/TextField.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import VButtonGroup from '@shared/component/VButtonGroup/VButtonGroup.vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import { allVariablesInStringExist } from '@shared/validation/vuelidate';
import { toDateonly } from '@tools/dates';
import { useInsertAtCursorPosition } from '@tools/insertAt';
import { toTypedSchema } from '@vee-validate/zod';
import { useConfirmDialog } from '@vueuse/core';
import { isAxiosError } from 'axios';
import { useForm } from 'vee-validate';
import { computed, onMounted, ref, toRefs } from 'vue';
import { z } from 'zod';

import AlertVariabelenButtons from '@alert-lib/AlertVariabelenButtons.vue';
import { frequentieOptions, ontvangerOptions } from '@alert-lib/alertOptions';
import { type PolisAlert } from './PolisAlert';

const props = defineProps<{
  alert: PolisAlert;
  relatienummer: number;
  adviseurnummer?: number;
  relatienaam?: string;
}>();

const emit = defineEmits<{
  formClosed: [success: boolean];
}>();

const { alert } = toRefs(props);

const textAreaRef = ref(null);

const { data: alertVariabelen } = usePolisAlertsGetAlertvariabelen({
  query: {
    select: (data) => data.result,
  },
});

const alertsVarLabels = computed(() => alertVariabelen.value?.map((r) => r.userFriendlyName) ?? []);

const validateVariabelen = (value: string) =>
  allVariablesInStringExist(value, alertsVarLabels.value);

const validationSchema = toTypedSchema(
  z.object({
    internPolisnummerPakket: z.string({ required_error: 'Selecteer een pakketpolis.' }),
    inhoud: z
      .string()
      .min(1, {
        message: 'Polisalert tekst is verplicht.',
      })
      .refine(validateVariabelen, {
        message: 'Variabele niet gevonden.',
      }),
    frequentie: z.nativeEnum(Frequentie),
    ontvangers: z.nativeEnum(Ontvanger).array().min(1, 'Kies minstens 1 ontvanger.'),
    ingangsdatum: z.date(),
  }),
);

const { errors, useFieldModel, validate, setErrors } = useForm({
  validationSchema,
  initialValues: {
    internPolisnummerPakket: alert.value.internPolisnummerPakket,
    inhoud: alert.value.inhoud,
    frequentie: alert.value.frequentie,
    ontvangers: alert.value.ontvangers,
    ingangsdatum: alert.value.ingangsdatum,
  },
});

const { insertAtCursorPosition } = useInsertAtCursorPosition(textAreaRef);
const internPolisnummerPakket = useFieldModel('internPolisnummerPakket');
const inhoud = useFieldModel('inhoud');
const frequentie = useFieldModel('frequentie');
const ontvangers = useFieldModel('ontvangers');
const ingangsdatum = useFieldModel('ingangsdatum');
const notification = useNotification();

const {
  isPending,
  isError,
  data: polissen,
} = usePolisGetPolissen(
  computed(() => ({
    relatienummer: props.relatienummer,
  })),
  {
    query: {
      select: (result) => result.result,
      staleTime: Infinity,
    },
  },
);

const isNew = computed(() => !props.alert.id);
const magOpslaan = computed(() =>
  isNew.value ? hasPermission('Alerts.AlertAanmaken') : hasPermission('Alerts.AlertBijwerken'),
);

const { isRevealed, reveal, confirm, cancel } = useConfirmDialog();

const { mutate: create, isPending: isCreating } = usePolisAlertsCreateForPolis({
  mutation: {
    onSuccess: () => confirm(),
    onError: handleError,
  },
});

const { mutate: update, isPending: isUpdating } = usePolisAlertsUpdate({
  mutation: {
    onSuccess: () => confirm(),
    onError: handleError,
  },
});

function handleError(e: any) {
  if (!isAxiosError(e)) return notification.error('Er is een onbekende fout opgetreden.');
  if (isProblemDetail(e.response)) {
    setErrors(e.response.data.errors);
    return;
  }
  if (e.stack === '403') notification.error('Geen rechten om een alert te maken of wijzigen');
  return notification.error(e.response?.data.title ?? 'Er is een onbekende fout opgetreden.');
}

async function submit() {
  const { valid } = await validate();
  if (!valid) return;

  if (!props.alert.id) {
    create({
      params: { adviseurnummer: props.adviseurnummer },
      data: {
        frequentie: frequentie.value!,
        inhoud: inhoud.value!,
        ontvangers: ontvangers.value!,
        ingangsdatum: toDateonly(ingangsdatum.value!),
        internpolisnummer: internPolisnummerPakket.value!,
        relatienummer: props.relatienummer,
        ingangsdatumGelijkAanPolis: false,
      },
    });
    return;
  }
  update({
    polisAlertId: props.alert.id,
    data: {
      frequentie: frequentie.value!,
      inhoud: inhoud.value!,
      ontvangers: ontvangers.value!,
      ingangsdatum: toDateonly(ingangsdatum.value!),
      relatienummer: props.relatienummer,
      ingangsdatumGelijkAanPolis: false,
    },
  });
}

const geselecteerdePolis = computed(() =>
  polissen.value?.find((e) => e.internPolisnummer === internPolisnummerPakket.value),
);

const options = computed(() =>
  polissen.value?.map((polis) => ({
    value: polis.internPolisnummer,
    label: `${polis.polisnummer} - ${polis.omschrijving}`,
  })),
);

onMounted(async () => {
  const res = await reveal();
  emit('formClosed', !res.isCanceled);
});
</script>

<template>
  <VSidePanel :open="isRevealed" @close="cancel" :width="800">
    <template #header>
      <VHeading class="text-white" :level="3">
        Alert {{ !isNew ? 'wijzigen' : 'aanmaken' }} voor {{ relatienaam }}
      </VHeading>
    </template>
    <template #default>
      <div class="flex flex-col gap-6">
        <VInfoBox v-if="!isPending && isError" type="danger">
          Er is een onbekende fout opgetreden bij het laden van de polissen.
        </VInfoBox>
        <VInfoBox v-if="!isPending && !polissen?.length && !isError" type="danger">
          Alerts kunnen alleen aangemaakt worden voor relaties die ten minste één pakketpolis
          hebben.
        </VInfoBox>
        <FieldRowGroup>
          <FieldRowV2
            label="Pakketpolis"
            :label-size="1"
            :errors="[errors.internPolisnummerPakket]"
          >
            <VSkeleton v-if="isPending" height="lg" />
            <SelectField
              v-else
              :has-error="!!errors.internPolisnummerPakket"
              :options="options"
              v-model:value="internPolisnummerPakket"
              :disabled="!isNew || !magOpslaan"
            />
            <VInfoBox v-if="geselecteerdePolis" class="w-full">
              <p>Dit pakket bevat de volgende polissen:</p>
              <ul class="ml-0">
                <li v-for="polis in geselecteerdePolis?.subpolissen" :key="polis.polisnummer">
                  {{ polis.polisnummer }} - {{ polis.omschrijving }}
                </li>
              </ul>
            </VInfoBox>
          </FieldRowV2>
          <AlertVariabelenButtons
            v-if="magOpslaan && !isPending && polissen?.length"
            @selected="insertAtCursorPosition"
          />
          <FieldRowV2 label="Tekst" :label-size="1" :errors="[errors.inhoud]">
            <TextField
              ref="textAreaRef"
              :has-error="!!errors.inhoud"
              :rows="6"
              placeholder="Vul je gewenste alerttekst in"
              :max-length="4000"
              :disabled="!magOpslaan"
              v-model:value="inhoud"
            />
          </FieldRowV2>
          <FieldRowV2 label="Frequentie" :label-size="1" :errors="[errors.frequentie]">
            <SelectField
              :has-error="!!errors.frequentie"
              :options="frequentieOptions"
              :disabled="!magOpslaan"
              v-model:value="frequentie"
            />
          </FieldRowV2>
          <FieldRowV2 label="Naar" :label-size="1" :errors="[errors.ontvangers]">
            <MultiCheckboxField
              :has-error="!!errors.ontvangers"
              path="ontvangers"
              :options="ontvangerOptions!"
              :disabled="!magOpslaan"
              v-model="ontvangers"
            />
          </FieldRowV2>
          <FieldRowV2 label="Ingangsdatum" :label-size="1" :errors="[errors.ingangsdatum]">
            <VDatePicker
              v-model="ingangsdatum"
              :invalid="!!errors.ingangsdatum"
              :disabled="!magOpslaan"
              :clearable="false"
            />
          </FieldRowV2>
        </FieldRowGroup>
      </div>
    </template>
    <template #footer>
      <VButtonGroup>
        <VButton
          prepend-icon="fas fa-save"
          @click="submit()"
          :disabled="!magOpslaan"
          :loading="isCreating || isUpdating"
        >
          Opslaan
        </VButton>
      </VButtonGroup>
    </template>
  </VSidePanel>
</template>
